import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  PushPin as PinIcon,
  PushPinOutlined as PinOutlinedIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  Chat as ChatIcon,
  ScreenShare as ScreenShareIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { Conversation } from '../store';

interface ConversationItemProps {
  conversation: Conversation;
  isActive: boolean;
  isCollapsed?: boolean;
  onSelect: (id: string) => void;
  onPin: (id: string) => void;
  onUnpin: (id: string) => void;
  onRename: (id: string, newTitle: string) => void;
  onDelete: (id: string) => void;
  onArchive: (id: string) => void;
}

const ConversationItem: React.FC<ConversationItemProps> = ({
  conversation,
  isActive,
  isCollapsed = false,
  onSelect,
  onPin,
  onUnpin,
  onRename,
  onDelete,
  onArchive,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(conversation.title);

  const handleContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handlePinToggle = () => {
    if (conversation.isPinned) {
      onUnpin(conversation._id);
    } else {
      onPin(conversation._id);
    }
    handleCloseMenu();
  };

  const handleRename = () => {
    setIsEditing(true);
    handleCloseMenu();
  };

  const handleSaveRename = () => {
    if (editTitle.trim() && editTitle !== conversation.title) {
      onRename(conversation._id, editTitle.trim());
    }
    setIsEditing(false);
  };

  const handleCancelRename = () => {
    setEditTitle(conversation.title);
    setIsEditing(false);
  };

  const handleDelete = () => {
    onDelete(conversation._id);
    handleCloseMenu();
  };

  const handleArchive = () => {
    onArchive(conversation._id);
    handleCloseMenu();
  };

  const formatDate = (date: Date | string) => {
    const d = new Date(date);
    const now = new Date();
    const diffInHours = (now.getTime() - d.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInHours < 168) { // 7 days
      return `${Math.floor(diffInHours / 24)}d ago`;
    } else {
      return d.toLocaleDateString();
    }
  };

  const getMessageCount = () => {
    return conversation.metadata?.messageCount || 0;
  };

  // Collapsed state - show only icon and tooltip
  if (isCollapsed) {
    return (
      <Tooltip title={conversation.title} placement="right">
        <IconButton
          onClick={() => onSelect(conversation._id)}
          sx={{
            width: 40,
            height: 40,
            mx: 0.5,
            mb: 0.5,
            bgcolor: isActive ? 'primary.main' : 'transparent',
            color: isActive ? 'white' : 'text.secondary',
            '&:hover': {
              bgcolor: isActive ? 'primary.dark' : 'action.hover',
            },
          }}
        >
          <ChatIcon />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        '&:hover .conversation-actions': {
          opacity: 1,
        },
      }}
    >
      <ListItemButton
        selected={isActive}
        onClick={() => onSelect(conversation._id)}
        onContextMenu={handleContextMenu}
        sx={{
          py: 1,
          px: 2,
          borderRadius: 1,
          mx: 1,
          mb: 0.5,
          minHeight: 48,
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.15),
            },
          },
        }}
      >
        {/* Remove the ListItemIcon section that renders the icon */}

        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? 'text.primary' : 'text.primary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}
              >
                {conversation.title}
              </Typography>
              
              {conversation.isPinned && (
                <PinIcon sx={{ fontSize: 16, color: 'warning.main' }} />
              )}
            </Box>
          }
          secondary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <TimeIcon sx={{ fontSize: 12 }} />
                {formatDate(conversation.updatedAt)}
              </Typography>
              
              {getMessageCount() > 0 && (
                <Chip
                  label={getMessageCount()}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 16,
                    fontSize: '0.625rem',
                    '& .MuiChip-label': {
                      px: 0.5,
                    },
                  }}
                />
              )}
            </Box>
          }
        />

        {/* Action buttons */}
        <Box
          className="conversation-actions"
          sx={{
            opacity: 0,
            transition: 'opacity 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Tooltip title={conversation.isPinned ? 'Unpin' : 'Pin'}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handlePinToggle();
              }}
              sx={{ p: 0.5 }}
            >
              {conversation.isPinned ? (
                <PinIcon sx={{ fontSize: 16, color: 'warning.main' }} />
              ) : (
                <PinOutlinedIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="More options">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleContextMenu(e);
              }}
              sx={{ p: 0.5 }}
            >
              <MoreVertIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </ListItemButton>

      {/* Context menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handlePinToggle}>
          <ListItemIcon>
            {conversation.isPinned ? <PinOutlinedIcon /> : <PinIcon />}
          </ListItemIcon>
          <ListItemText>
            {conversation.isPinned ? 'Unpin' : 'Pin'}
          </ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleRename}>
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleArchive}>
          <ListItemIcon>
            <ArchiveIcon />
          </ListItemIcon>
          <ListItemText>Archive</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <ListItemIcon sx={{ color: 'error.main' }}>
            <DeleteIcon />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ConversationItem; 